import React, { useState, useEffect } from 'react';
import {
  Box, Container, Typography, Paper, Grid, Card, CardContent,
  Tabs, Tab, Chip, IconButton, Tooltip, TextField, Button,
  CircularProgress, Divider, Dialog, DialogTitle, DialogContent,
  DialogActions, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Breadcrumbs, Link
} from '@mui/material';
import {
  Refresh as RefreshIcon, Print as PrintIcon, AttachMoney as MoneyIcon,
  Factory as FactoryIcon, ShoppingCart as ProcurementIcon,
  Inventory as InventoryIcon, PointOfSale as SalesIcon,
  LocalShipping as DispatchIcon, TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon, GetApp as ExportIcon,
  ArrowBack as ArrowBackIcon, NavigateNext as NavigateNextIcon,
  Home as HomeIcon, Timeline as TimelineIcon
} from '@mui/icons-material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area, Line,
  ComposedChart, Rectangle
} from 'recharts';
import api from '../services/api';
import { toast } from 'react-hot-toast';
import { format, subDays } from 'date-fns';
import { useNavigate } from 'react-router-dom';

const COLORS = ['#2E7D32', '#FF8F00', '#1976D2', '#D32F2F', '#7B1FA2', '#0097A7', '#F57C00', '#388E3C', '#C62828', '#4527A0'];

const categoryLabel = (cat) => {
  if (!cat) return 'Uncategorized';
  if (typeof cat === 'string') return cat;
  if (typeof cat === 'object') return cat.name || 'Uncategorized';
  return 'Uncategorized';
};

// Build a drill-down payload with explicit column -> key mapping.
// Avoids fragile string munging of headers like "Oil (L)".
const buildDrillDown = (details, columns) => {
  const keys = columns.map(c =>
    c.toLowerCase()
      .replace(/\s*\([^)]*\)\s*/g, '')   // drop "(L)", "(kg)", "₹", etc.
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_|_$/g, '')
  );
  return { details, columns, keys };
};

const ExecutiveDashboard = () => {
  const [loading, setLoading] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [dateRange, setDateRange] = useState({
    startDate: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd')
  });
  const [drillDownData, setDrillDownData] = useState(null);
  const [drillDownOpen, setDrillDownOpen] = useState(false);
  const [drillDownTitle, setDrillDownTitle] = useState('');
  const [drillDownLoading, setDrillDownLoading] = useState(false);
  const [selectedDataPoint, setSelectedDataPoint] = useState(null);

  const navigate = useNavigate();

  const [financeData, setFinanceData] = useState({
    totalReceived: 0, totalPaid: 0, totalExpenses: 0, netBalance: 0,
    paymentTrend: [], categoryBreakdown: [], allPayments: []
  });
  const [productionData, setProductionData] = useState({
    totalBatches: 0, totalOilOutput: 0, totalCakeOutput: 0, totalSludgeOutput: 0,
    batchTrend: [], shiftDistribution: [], machinePerformance: [], allBatches: []
  });
  const [procurementData, setProcurementData] = useState({
    totalOrders: 0, totalAmount: 0, pendingOrders: 0,
    orderTrend: [], supplierBreakdown: [], statusDistribution: [], allOrders: []
  });
  const [inventoryData, setInventoryData] = useState({
    totalProducts: 0, lowStockItems: 0, totalValue: 0,
    categoryDistribution: [], topProducts: [], lowStockList: [], allProducts: []
  });
  const [salesData, setSalesData] = useState({
    totalOrders: 0, totalRevenue: 0, pendingOrders: 0,
    orderTrend: [], customerBreakdown: [], statusDistribution: [], allOrders: []
  });
  const [tripData, setTripData] = useState({
    totalTrips: 0, totalMileage: 0, completedTrips: 0,
    tripTrend: [], vehicleUtilization: [], statusDistribution: [], allTrips: []
  });

  const tabs = [
    { label: 'Finance', icon: <MoneyIcon /> },
    { label: 'Production', icon: <FactoryIcon /> },
    { label: 'Procurement', icon: <ProcurementIcon /> },
    { label: 'Inventory', icon: <InventoryIcon /> },
    { label: 'Sales', icon: <SalesIcon /> },
    { label: 'Trip Management', icon: <DispatchIcon /> }
  ];

  useEffect(() => { fetchAllData(); }, [dateRange]);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchFinanceData(), fetchProductionData(), fetchProcurementData(),
        fetchInventoryData(), fetchSalesData(), fetchTripData()
      ]);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchFinanceData = async () => {
    try {
      const [paymentsRes, expensesRes] = await Promise.all([
        api.get('/finance/payments/', { params: { start_date: dateRange.startDate, end_date: dateRange.endDate } }),
        api.get('/finance/expenses/', { params: { start_date: dateRange.startDate, end_date: dateRange.endDate } })
      ]);
      const payments = Array.isArray(paymentsRes.data) ? paymentsRes.data : paymentsRes.data?.results || [];
      const expenses = Array.isArray(expensesRes.data) ? expensesRes.data : expensesRes.data?.results || [];

      let totalReceived = 0, totalPaid = 0;
      payments.forEach(p => {
        if (p.payment_type === 'received') totalReceived += parseFloat(p.amount);
        else totalPaid += parseFloat(p.amount);
      });
      const totalExpenses = expenses.reduce((sum, e) => sum + parseFloat(e.amount), 0);

      const trendMap = {};
      payments.forEach(p => {
        const date = format(new Date(p.payment_date), 'yyyy-MM-dd');
        if (!trendMap[date]) trendMap[date] = { received: 0, paid: 0 };
        if (p.payment_type === 'received') trendMap[date].received += parseFloat(p.amount);
        else trendMap[date].paid += parseFloat(p.amount);
      });
      const paymentTrend = Object.keys(trendMap).map(date => ({
        date, received: trendMap[date].received, paid: trendMap[date].paid
      })).sort((a, b) => a.date.localeCompare(b.date));

      const categoryMap = {};
      expenses.forEach(e => {
        const cat = e.category || 'others';
        if (!categoryMap[cat]) categoryMap[cat] = 0;
        categoryMap[cat] += parseFloat(e.amount);
      });
      const categoryBreakdown = Object.keys(categoryMap).map(cat => ({
        name: cat.replace('_', ' ').toUpperCase(), value: categoryMap[cat]
      }));

      setFinanceData({
        totalReceived, totalPaid, totalExpenses,
        netBalance: totalReceived - totalPaid - totalExpenses,
        paymentTrend, categoryBreakdown, allPayments: payments
      });
    } catch (error) {
      console.error('Error fetching finance data:', error);
    }
  };

  const fetchProductionData = async () => {
    try {
      const response = await api.get('/production/batches/', {
        params: { start_date: dateRange.startDate, end_date: dateRange.endDate }
      });
      const batches = Array.isArray(response.data) ? response.data : response.data?.results || [];

      let totalOilOutput = 0, totalCakeOutput = 0, totalSludgeOutput = 0;
      const batchTrend = [];
      const shiftMap = {};
      const machineMap = {};

      batches.forEach(b => {
        const oil = parseFloat(b.oil_output || 0);
        const cake = parseFloat(b.cake_output || 0);
        const sludge = parseFloat(b.sludge_output || 0);

        totalOilOutput += oil;
        totalCakeOutput += cake;
        totalSludgeOutput += sludge;

        const date = format(new Date(b.production_date || b.created_at), 'yyyy-MM-dd');
        const existing = batchTrend.find(item => item.date === date);
        if (existing) {
          existing.oil += oil;
          existing.cake += cake;
          existing.sludge += sludge;
        } else {
          batchTrend.push({ date, oil, cake, sludge });
        }

        const shift = b.shift || 'unknown';
        shiftMap[shift] = (shiftMap[shift] || 0) + 1;

        const machine = b.machine_id || 'unknown';
        machineMap[machine] = (machineMap[machine] || 0) + 1;
      });

      const shiftDistribution = Object.keys(shiftMap).map(key => ({
        name: key.replace('_', ' ').toUpperCase(), value: shiftMap[key]
      }));
      const machinePerformance = Object.keys(machineMap).map(key => ({
        name: key, value: machineMap[key]
      })).sort((a, b) => b.value - a.value).slice(0, 10);

      setProductionData({
        totalBatches: batches.length,
        totalOilOutput, totalCakeOutput, totalSludgeOutput,
        batchTrend: batchTrend.sort((a, b) => a.date.localeCompare(b.date)),
        shiftDistribution, machinePerformance,
        allBatches: batches
      });
    } catch (error) {
      console.error('Error fetching production data:', error);
    }
  };

  const fetchProcurementData = async () => {
    try {
      const response = await api.get('/procurement/orders/', {
        params: { start_date: dateRange.startDate, end_date: dateRange.endDate }
      });
      const orders = Array.isArray(response.data) ? response.data : response.data?.results || [];

      let totalAmount = 0, pendingOrders = 0;
      const orderTrend = [], supplierMap = {}, statusMap = {};

      orders.forEach(o => {
        totalAmount += parseFloat(o.total_amount || 0);
        if (o.status === 'pending' || o.status === 'draft') pendingOrders++;

        const date = format(new Date(o.created_at || o.order_date), 'yyyy-MM-dd');
        const existing = orderTrend.find(item => item.date === date);
        if (existing) {
          existing.amount += parseFloat(o.total_amount || 0);
          existing.count += 1;
        } else {
          orderTrend.push({ date, amount: parseFloat(o.total_amount || 0), count: 1 });
        }

        const supplier = o.supplier_details?.name || o.supplier_name || 'Unknown';
        supplierMap[supplier] = (supplierMap[supplier] || 0) + parseFloat(o.total_amount || 0);

        const status = o.status || 'draft';
        statusMap[status] = (statusMap[status] || 0) + 1;
      });

      setProcurementData({
        totalOrders: orders.length, totalAmount, pendingOrders,
        orderTrend: orderTrend.sort((a, b) => a.date.localeCompare(b.date)),
        supplierBreakdown: Object.keys(supplierMap).map(key => ({ name: key, value: supplierMap[key] }))
          .sort((a, b) => b.value - a.value).slice(0, 10),
        statusDistribution: Object.keys(statusMap).map(key => ({ name: key.toUpperCase(), value: statusMap[key] })),
        allOrders: orders
      });
    } catch (error) {
      console.error('Error fetching procurement data:', error);
    }
  };

  const fetchInventoryData = async () => {
    try {
      const response = await api.get('/inventory/products/');
      const products = Array.isArray(response.data) ? response.data : response.data?.results || [];

      let totalValue = 0, lowStockItems = 0;
      const categoryMap = {}, topProducts = [], lowStockList = [];

      products.forEach(p => {
        const stock = parseFloat(p.current_stock || 0);
        const price = parseFloat(p.purchase_price || 0);
        const reorderLevel = parseFloat(p.reorder_level || 0);
        totalValue += stock * price;

        if (stock <= reorderLevel) {
          lowStockItems++;
          lowStockList.push({ name: p.name || 'Unknown', stock, reorderLevel, price });
        }

        const categoryName = categoryLabel(p.category);
        categoryMap[categoryName] = (categoryMap[categoryName] || 0) + stock;
        topProducts.push({ name: p.name || 'Unknown', stock, value: stock * price });
      });

      setInventoryData({
        totalProducts: products.length,
        lowStockItems, totalValue,
        categoryDistribution: Object.keys(categoryMap).map(key => ({ name: key, value: categoryMap[key] })),
        topProducts: topProducts.sort((a, b) => b.value - a.value).slice(0, 10),
        lowStockList: lowStockList.sort((a, b) => a.stock - b.stock),
        allProducts: products
      });
    } catch (error) {
      console.error('Error fetching inventory data:', error);
    }
  };

  const fetchSalesData = async () => {
    try {
      const response = await api.get('/sales/orders/', {
        params: { start_date: dateRange.startDate, end_date: dateRange.endDate }
      });
      const orders = Array.isArray(response.data) ? response.data : response.data?.results || [];

      let totalRevenue = 0, pendingOrders = 0;
      const orderTrend = [], customerMap = {}, statusMap = {};

      orders.forEach(o => {
        const amount = parseFloat(o.net_amount || o.total_amount || 0);
        totalRevenue += amount;
        if (o.status === 'pending' || o.status === 'draft') pendingOrders++;

        const date = format(new Date(o.created_at || o.order_date), 'yyyy-MM-dd');
        const existing = orderTrend.find(item => item.date === date);
        if (existing) { existing.amount += amount; existing.count += 1; }
        else orderTrend.push({ date, amount, count: 1 });

        const customer = o.customer?.name || o.customer_name || 'Unknown';
        customerMap[customer] = (customerMap[customer] || 0) + amount;

        const status = o.status || 'draft';
        statusMap[status] = (statusMap[status] || 0) + 1;
      });

      setSalesData({
        totalOrders: orders.length, totalRevenue, pendingOrders,
        orderTrend: orderTrend.sort((a, b) => a.date.localeCompare(b.date)),
        customerBreakdown: Object.keys(customerMap).map(key => ({ name: key, value: customerMap[key] }))
          .sort((a, b) => b.value - a.value).slice(0, 10),
        statusDistribution: Object.keys(statusMap).map(key => ({ name: key.toUpperCase(), value: statusMap[key] })),
        allOrders: orders
      });
    } catch (error) {
      console.error('Error fetching sales data:', error);
    }
  };

  const fetchTripData = async () => {
    try {
      const response = await api.get('/dispatch/trips/', {
        params: { start_date: dateRange.startDate, end_date: dateRange.endDate }
      });
      const trips = Array.isArray(response.data) ? response.data : response.data?.results || [];

      let totalMileage = 0, completedTrips = 0;
      const tripTrend = [], vehicleMap = {}, statusMap = {};

      trips.forEach(t => {
        totalMileage += parseFloat(t.total_mileage || 0);
        if (t.status === 'completed') completedTrips++;

        const date = format(new Date(t.start_date), 'yyyy-MM-dd');
        const existing = tripTrend.find(item => item.date === date);
        if (existing) { existing.count += 1; existing.mileage += parseFloat(t.total_mileage || 0); }
        else tripTrend.push({ date, count: 1, mileage: parseFloat(t.total_mileage || 0) });

        const vehicle = t.vehicle_number || t.vehicle?.vehicle_number || 'Unknown';
        vehicleMap[vehicle] = (vehicleMap[vehicle] || 0) + parseFloat(t.total_mileage || 0);

        const status = t.status || 'planned';
        statusMap[status] = (statusMap[status] || 0) + 1;
      });

      setTripData({
        totalTrips: trips.length, totalMileage, completedTrips,
        tripTrend: tripTrend.sort((a, b) => a.date.localeCompare(b.date)),
        vehicleUtilization: Object.keys(vehicleMap).map(key => ({ name: key, value: vehicleMap[key] }))
          .sort((a, b) => b.value - a.value).slice(0, 10),
        statusDistribution: Object.keys(statusMap).map(key => ({ name: key.replace('_', ' ').toUpperCase(), value: statusMap[key] })),
        allTrips: trips
      });
    } catch (error) {
      console.error('Error fetching trip data:', error);
    }
  };

  const handleDrillDown = (data, type, dataPoint = null) => {
    setSelectedDataPoint(dataPoint);
    setDrillDownTitle(type);
    setDrillDownLoading(true);

    let details = [];
    let columns = [];

    switch (type) {
      case 'Total Received':
        details = financeData.allPayments.filter(p => p.payment_type === 'received').map(p => ({
          date: format(new Date(p.payment_date), 'dd/MM/yyyy HH:mm'),
          type: p.payment_type.toUpperCase(),
          amount: parseFloat(p.amount),
          party: p.customer?.name || p.customer_name || 'N/A',
          method: p.payment_method?.toUpperCase() || 'N/A',
          settlement: p.settlement_type?.toUpperCase() || 'N/A'
        }));
        columns = ['Date', 'Type', 'Party', 'Amount', 'Method', 'Settlement'];
        break;

      case 'Total Paid':
        details = financeData.allPayments.filter(p => p.payment_type === 'paid').map(p => ({
          date: format(new Date(p.payment_date), 'dd/MM/yyyy HH:mm'),
          type: p.payment_type.toUpperCase(),
          amount: parseFloat(p.amount),
          party: p.supplier?.name || p.supplier_name || 'N/A',
          method: p.payment_method?.toUpperCase() || 'N/A',
          settlement: p.settlement_type?.toUpperCase() || 'N/A'
        }));
        columns = ['Date', 'Type', 'Party', 'Amount', 'Method', 'Settlement'];
        break;

      case 'Payment Trend': {
        const filter = (p) => !dataPoint || !dataPoint.date ||
          format(new Date(p.payment_date), 'yyyy-MM-dd') === dataPoint.date;
        details = financeData.allPayments.filter(filter).map(p => ({
          date: format(new Date(p.payment_date), 'dd/MM/yyyy HH:mm'),
          type: p.payment_type.toUpperCase(),
          amount: parseFloat(p.amount),
          party: p.customer?.name || p.supplier?.name || 'N/A',
          method: p.payment_method?.toUpperCase() || 'N/A',
          settlement: p.settlement_type?.toUpperCase() || 'N/A'
        }));
        columns = ['Date', 'Type', 'Party', 'Amount', 'Method', 'Settlement'];
        break;
      }

      case 'Expense Categories':
        if (dataPoint && dataPoint.name) {
          details = [{
            category: dataPoint.name, amount: dataPoint.value,
            percentage: ((dataPoint.value / financeData.totalExpenses) * 100).toFixed(1)
          }];
        } else {
          details = financeData.categoryBreakdown.map(c => ({
            category: c.name, amount: c.value,
            percentage: ((c.value / financeData.totalExpenses) * 100).toFixed(1)
          }));
        }
        columns = ['Category', 'Amount', 'Percentage'];
        break;

      case 'Production Trend': {
        const filter = (b) => !dataPoint || !dataPoint.date ||
          format(new Date(b.production_date || b.created_at), 'yyyy-MM-dd') === dataPoint.date;
        details = productionData.allBatches.filter(filter).map(b => ({
          batch: b.batch_number || b.id || 'N/A',
          date: format(new Date(b.production_date || b.created_at), 'dd/MM/yyyy'),
          oil: parseFloat(b.oil_output || 0),
          cake: parseFloat(b.cake_output || 0),
          sludge: parseFloat(b.sludge_output || 0),
          shift: b.shift || 'N/A',
          machine: b.machine_id || 'N/A'
        }));
        columns = ['Batch', 'Date', 'Oil (L)', 'Cake (kg)', 'Sludge (kg)', 'Shift', 'Machine'];
        break;
      }

      case 'Shift Distribution':
        if (dataPoint && dataPoint.name) {
          const shiftName = dataPoint.name;
          details = productionData.allBatches
            .filter(b => (b.shift || 'unknown').replace('_', ' ').toUpperCase() === shiftName)
            .map(b => ({
              batch: b.batch_number || b.id || 'N/A',
              date: format(new Date(b.production_date || b.created_at), 'dd/MM/yyyy'),
              shift: b.shift || 'N/A',
              oil: parseFloat(b.oil_output || 0),
              cake: parseFloat(b.cake_output || 0),
              sludge: parseFloat(b.sludge_output || 0)
            }));
          columns = ['Batch', 'Date', 'Shift', 'Oil (L)', 'Cake (kg)', 'Sludge (kg)'];
        } else {
          details = productionData.shiftDistribution.map(s => ({
            shift: s.name, batches: s.value,
            percentage: ((s.value / productionData.totalBatches) * 100).toFixed(1)
          }));
          columns = ['Shift', 'Batches', 'Percentage'];
        }
        break;

      case 'Machine Performance':
        if (dataPoint && dataPoint.name) {
          const machineId = dataPoint.name;
          details = productionData.allBatches.filter(b => b.machine_id === machineId).map(b => ({
            batch: b.batch_number || b.id || 'N/A',
            date: format(new Date(b.production_date || b.created_at), 'dd/MM/yyyy'),
            machine: b.machine_id || 'N/A',
            oil: parseFloat(b.oil_output || 0),
            cake: parseFloat(b.cake_output || 0),
            sludge: parseFloat(b.sludge_output || 0)
          }));
          columns = ['Batch', 'Date', 'Machine', 'Oil (L)', 'Cake (kg)', 'Sludge (kg)'];
        } else {
          details = productionData.machinePerformance.map(m => ({
            machine: m.name, batches: m.value,
            percentage: ((m.value / productionData.totalBatches) * 100).toFixed(1)
          }));
          columns = ['Machine', 'Batches', 'Percentage'];
        }
        break;

      case 'Purchase Orders Trend':
        details = procurementData.allOrders
          .filter(o => !dataPoint || !dataPoint.date ||
            format(new Date(o.created_at || o.order_date), 'yyyy-MM-dd') === dataPoint.date)
          .map(o => ({
            order: o.po_number || o.id,
            date: format(new Date(o.created_at || o.order_date), 'dd/MM/yyyy'),
            supplier: o.supplier_details?.name || o.supplier_name || 'N/A',
            amount: parseFloat(o.total_amount || 0),
            status: o.status?.toUpperCase() || 'DRAFT'
          }));
        columns = ['Order #', 'Date', 'Supplier', 'Amount', 'Status'];
        break;

      case 'Supplier Breakdown':
        if (dataPoint && dataPoint.name) {
          const supplierName = dataPoint.name;
          details = procurementData.allOrders
            .filter(o => (o.supplier_details?.name || o.supplier_name) === supplierName)
            .map(o => ({
              order: o.po_number || o.id,
              date: format(new Date(o.created_at || o.order_date), 'dd/MM/yyyy'),
              supplier: o.supplier_details?.name || o.supplier_name || 'N/A',
              amount: parseFloat(o.total_amount || 0),
              status: o.status?.toUpperCase() || 'DRAFT'
            }));
          columns = ['Order #', 'Date', 'Supplier', 'Amount', 'Status'];
        } else {
          details = procurementData.supplierBreakdown.map(s => ({
            supplier: s.name, amount: s.value,
            percentage: ((s.value / procurementData.totalAmount) * 100).toFixed(1)
          }));
          columns = ['Supplier', 'Amount', 'Percentage'];
        }
        break;

      case 'Sales Trend':
        details = salesData.allOrders
          .filter(o => !dataPoint || !dataPoint.date ||
            format(new Date(o.created_at || o.order_date), 'yyyy-MM-dd') === dataPoint.date)
          .map(o => ({
            order: o.so_number || o.id,
            date: format(new Date(o.created_at || o.order_date), 'dd/MM/yyyy'),
            customer: o.customer?.name || o.customer_name || 'N/A',
            amount: parseFloat(o.net_amount || o.total_amount || 0),
            status: o.status?.toUpperCase() || 'DRAFT'
          }));
        columns = ['Order #', 'Date', 'Customer', 'Amount', 'Status'];
        break;

      case 'Customer Breakdown':
        if (dataPoint && dataPoint.name) {
          const customerName = dataPoint.name;
          details = salesData.allOrders
            .filter(o => (o.customer?.name || o.customer_name) === customerName)
            .map(o => ({
              order: o.so_number || o.id,
              date: format(new Date(o.created_at || o.order_date), 'dd/MM/yyyy'),
              customer: o.customer?.name || o.customer_name || 'N/A',
              amount: parseFloat(o.net_amount || o.total_amount || 0),
              status: o.status?.toUpperCase() || 'DRAFT'
            }));
          columns = ['Order #', 'Date', 'Customer', 'Amount', 'Status'];
        } else {
          details = salesData.customerBreakdown.map(c => ({
            customer: c.name, revenue: c.value,
            percentage: ((c.value / salesData.totalRevenue) * 100).toFixed(1)
          }));
          columns = ['Customer', 'Revenue', 'Percentage'];
        }
        break;

      case 'Trip Trends':
        details = tripData.allTrips
          .filter(t => !dataPoint || !dataPoint.date ||
            format(new Date(t.start_date), 'yyyy-MM-dd') === dataPoint.date)
          .map(t => ({
            trip: t.trip_number,
            date: format(new Date(t.start_date), 'dd/MM/yyyy'),
            vehicle: t.vehicle_number || t.vehicle?.vehicle_number || 'N/A',
            driver: t.driver_name || t.driver?.name || 'N/A',
            mileage: parseFloat(t.total_mileage || 0),
            status: t.status?.toUpperCase() || 'PLANNED'
          }));
        columns = ['Trip #', 'Date', 'Vehicle', 'Driver', 'Mileage (km)', 'Status'];
        break;

      case 'Vehicle Utilization':
        if (dataPoint && dataPoint.name) {
          const vehicleName = dataPoint.name;
          details = tripData.allTrips
            .filter(t => (t.vehicle_number || t.vehicle?.vehicle_number) === vehicleName)
            .map(t => ({
              trip: t.trip_number,
              date: format(new Date(t.start_date), 'dd/MM/yyyy'),
              vehicle: t.vehicle_number || t.vehicle?.vehicle_number || 'N/A',
              driver: t.driver_name || t.driver?.name || 'N/A',
              mileage: parseFloat(t.total_mileage || 0),
              status: t.status?.toUpperCase() || 'PLANNED'
            }));
          columns = ['Trip #', 'Date', 'Vehicle', 'Driver', 'Mileage (km)', 'Status'];
        } else {
          details = tripData.vehicleUtilization.map(v => ({
            vehicle: v.name, mileage: v.value,
            percentage: ((v.value / tripData.totalMileage) * 100).toFixed(1)
          }));
          columns = ['Vehicle', 'Mileage', 'Percentage'];
        }
        break;

      default:
        details = [];
    }

    // If a specific case needs a custom key set, we can override here.
    // Otherwise the generic builder derives keys from column headers.
    const payload = buildDrillDown(details, columns);

    // Some rows use keys that don't match header-derived keys (e.g. Amount->amount is fine,
    // but Percentage->percentage, Order #->order). buildDrillDown handles those.
    setDrillDownData(payload);
    setDrillDownLoading(false);
    setDrillDownOpen(true);
  };

  const handleTabChange = (event, newValue) => setSelectedTab(newValue);
  const handleDateChange = (field) => (event) => setDateRange(prev => ({ ...prev, [field]: event.target.value }));
  const handleRefresh = () => fetchAllData();

  const handleExportData = () => {
    if (drillDownData && drillDownData.details.length > 0) {
      const headers = drillDownData.columns.join(',');
      const rows = drillDownData.details.map(row =>
        drillDownData.columns.map((col, i) => {
          const key = drillDownData.keys[i];
          const value = row[key] !== undefined ? row[key] : '';
          return typeof value === 'string' && value.includes(',') ? `"${value}"` : value;
        }).join(',')
      );
      const csv = [headers, ...rows].join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${drillDownTitle.replace(/ /g, '_')}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Data exported successfully');
    } else {
      toast.info('No data to export');
    }
  };

  const handlePrint = () => window.print();
  const handleCloseDrillDown = () => {
    setDrillDownOpen(false);
    setDrillDownData(null);
    setSelectedDataPoint(null);
  };

  const StatCard = ({ title, value, subtitle, color, icon, trend, onClick }) => (
    <Card sx={{ height: '100%', cursor: onClick ? 'pointer' : 'default' }} onClick={onClick}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>{title}</Typography>
            <Typography variant="h4" fontWeight="bold" color={color}>
              {typeof value === 'number' ? value.toLocaleString() : value}
            </Typography>
            {subtitle && <Typography variant="caption" color="textSecondary">{subtitle}</Typography>}
          </Box>
          <Box sx={{ bgcolor: color + '20', borderRadius: '50%', p: 1.5 }}>{icon}</Box>
        </Box>
        {trend && (
          <Box mt={1} display="flex" alignItems="center">
            {trend > 0 ? <TrendingUpIcon sx={{ color: 'success.main', fontSize: 16, mr: 0.5 }} />
                       : <TrendingDownIcon sx={{ color: 'error.main', fontSize: 16, mr: 0.5 }} />}
            <Typography variant="caption" color={trend > 0 ? 'success.main' : 'error.main'}>
              {Math.abs(trend)}% from last period
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  const CustomBar = (props) => {
    const { x, y, width, height, onClick, payload, fill } = props;
    return (
      <Rectangle x={x} y={y} width={width} height={height} fill={fill}
        onClick={() => onClick && onClick(payload)} style={{ cursor: 'pointer' }} />
    );
  };

  const renderFinanceDashboard = () => (
    <Box>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Total Received" value={financeData.totalReceived} color="#2E7D32"
            icon={<MoneyIcon />} onClick={() => handleDrillDown(null, 'Total Received')} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Total Paid" value={financeData.totalPaid} color="#D32F2F"
            icon={<MoneyIcon />} onClick={() => handleDrillDown(null, 'Total Paid')} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Total Expenses" value={financeData.totalExpenses} color="#ED6C02"
            icon={<MoneyIcon />} onClick={() => handleDrillDown(null, 'Expense Categories')} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Net Balance" value={financeData.netBalance}
            color={financeData.netBalance >= 0 ? '#2E7D32' : '#D32F2F'} icon={<MoneyIcon />} />
        </Grid>
      </Grid>
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Payment Trend</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={financeData.paymentTrend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" /><YAxis /><RechartsTooltip /><Legend />
                <Bar dataKey="received" fill="#2E7D32" name="Received"
                  shape={<CustomBar onClick={(payload) => handleDrillDown(payload, 'Payment Trend', payload)} />} />
                <Bar dataKey="paid" fill="#D32F2F" name="Paid"
                  shape={<CustomBar onClick={(payload) => handleDrillDown(payload, 'Payment Trend', payload)} />} />
              </ComposedChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Expense Breakdown</Typography>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={financeData.categoryBreakdown} cx="50%" cy="50%" labelLine={false}
                  outerRadius={80} fill="#8884d8" dataKey="value"
                  onClick={(data) => handleDrillDown(data, 'Expense Categories', data)}
                  style={{ cursor: 'pointer' }}>
                  {financeData.categoryBreakdown.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <RechartsTooltip /><Legend />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );

  const renderProductionDashboard = () => (
    <Box>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Total Batches" value={productionData.totalBatches}
            color="#1976D2" icon={<FactoryIcon />}
            onClick={() => handleDrillDown(null, 'Production Trend')} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Oil Output (L)" value={productionData.totalOilOutput.toFixed(2)}
            color="#FFD700" icon={<FactoryIcon />} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Cake Output (kg)" value={productionData.totalCakeOutput.toFixed(2)}
            color="#8B7355" icon={<FactoryIcon />} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Sludge Output (kg)" value={productionData.totalSludgeOutput.toFixed(2)}
            color="#808080" icon={<FactoryIcon />} />
        </Grid>
      </Grid>
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Production Trend</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={productionData.batchTrend}
                onClick={(data) => {
                  if (data?.payload?.[data.activeTooltipIndex]?.payload) {
                    handleDrillDown(data.payload[data.activeTooltipIndex].payload, 'Production Trend',
                      data.payload[data.activeTooltipIndex].payload);
                  }
                }}
                style={{ cursor: 'pointer' }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" /><YAxis /><RechartsTooltip /><Legend />
                <Area type="monotone" dataKey="oil" stackId="1" stroke="#FFD700" fill="#FFD700" name="Oil" />
                <Area type="monotone" dataKey="cake" stackId="1" stroke="#8B7355" fill="#8B7355" name="Cake" />
                <Area type="monotone" dataKey="sludge" stackId="1" stroke="#808080" fill="#808080" name="Sludge" />
              </AreaChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Shift Distribution</Typography>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={productionData.shiftDistribution} cx="50%" cy="50%" labelLine={false}
                  outerRadius={80} fill="#8884d8" dataKey="value"
                  onClick={(data) => handleDrillDown(data, 'Shift Distribution', data)}
                  style={{ cursor: 'pointer' }}>
                  {productionData.shiftDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <RechartsTooltip /><Legend />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Machine Performance</Typography>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={productionData.machinePerformance}
                onClick={(data) => {
                  if (data?.payload?.[data.activeTooltipIndex]?.payload) {
                    handleDrillDown(data.payload[data.activeTooltipIndex].payload, 'Machine Performance',
                      data.payload[data.activeTooltipIndex].payload);
                  }
                }} style={{ cursor: 'pointer' }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" /><YAxis /><RechartsTooltip />
                <Bar dataKey="value" fill="#1976D2" name="Batches"
                  shape={<CustomBar onClick={(payload) => handleDrillDown(payload, 'Machine Performance', payload)} />} />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );

  const renderProcurementDashboard = () => (
    <Box>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Total Orders" value={procurementData.totalOrders} color="#1976D2"
            icon={<ProcurementIcon />} onClick={() => handleDrillDown(null, 'Purchase Orders Trend')} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Total Amount" value={procurementData.totalAmount.toFixed(2)}
            color="#2E7D32" icon={<MoneyIcon />} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Pending Orders" value={procurementData.pendingOrders}
            color="#ED6C02" icon={<ProcurementIcon />} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Avg Order Value"
            value={(procurementData.totalAmount / (procurementData.totalOrders || 1)).toFixed(2)}
            color="#7B1FA2" icon={<MoneyIcon />} />
        </Grid>
      </Grid>
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Purchase Orders Trend</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={procurementData.orderTrend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" /><YAxis /><RechartsTooltip /><Legend />
                <Bar dataKey="amount" fill="#1976D2" name="Amount"
                  shape={<CustomBar onClick={(payload) => handleDrillDown(payload, 'Purchase Orders Trend', payload)} />} />
                <Line type="monotone" dataKey="count" stroke="#FF8F00" name="Orders" />
              </ComposedChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Status Distribution</Typography>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie data={procurementData.statusDistribution} cx="50%" cy="50%"
                  labelLine={false} outerRadius={80} fill="#8884d8" dataKey="value">
                  {procurementData.statusDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <RechartsTooltip /><Legend />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Top Suppliers</Typography>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={procurementData.supplierBreakdown}
                onClick={(data) => {
                  if (data?.payload?.[data.activeTooltipIndex]?.payload) {
                    handleDrillDown(data.payload[data.activeTooltipIndex].payload, 'Supplier Breakdown',
                      data.payload[data.activeTooltipIndex].payload);
                  }
                }} style={{ cursor: 'pointer' }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" /><YAxis /><RechartsTooltip />
                <Bar dataKey="value" fill="#2E7D32" name="Amount"
                  shape={<CustomBar onClick={(payload) => handleDrillDown(payload, 'Supplier Breakdown', payload)} />} />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );

  const renderInventoryDashboard = () => (
    <Box>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Total Products" value={inventoryData.totalProducts} color="#1976D2"
            icon={<InventoryIcon />}
            onClick={() => {
              setDrillDownTitle('All Products');
              setDrillDownData(buildDrillDown(
                inventoryData.allProducts.map(p => ({
                  name: p.name || 'Unknown',
                  stock: parseFloat(p.current_stock || 0),
                  price: parseFloat(p.purchase_price || 0),
                  selling_price: parseFloat(p.selling_price || 0),
                  category: categoryLabel(p.category),
                  reorder_level: parseFloat(p.reorder_level || 0),
                  status: p.is_active ? 'Active' : 'Inactive'
                })),
                ['Name', 'Stock', 'Price', 'Selling Price', 'Category', 'Reorder Level', 'Status']
              ));
              setDrillDownOpen(true);
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Total Stock Value" value={inventoryData.totalValue.toFixed(2)}
            color="#2E7D32" icon={<MoneyIcon />} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Low Stock Items" value={inventoryData.lowStockItems}
            color="#D32F2F" icon={<InventoryIcon />}
            onClick={() => {
              setDrillDownTitle('Low Stock Items');
              setDrillDownData(buildDrillDown(
                inventoryData.lowStockList.map(p => ({
                  product: p.name, stock: p.stock,
                  reorder_level: p.reorderLevel, price: p.price
                })),
                ['Product', 'Stock', 'Reorder Level', 'Price']
              ));
              setDrillDownOpen(true);
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Categories" value={inventoryData.categoryDistribution.length}
            color="#7B1FA2" icon={<InventoryIcon />} />
        </Grid>
      </Grid>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Category Distribution</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={inventoryData.categoryDistribution} cx="50%" cy="50%"
                  labelLine={false} outerRadius={80} fill="#8884d8" dataKey="value"
                  style={{ cursor: 'pointer' }}>
                  {inventoryData.categoryDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <RechartsTooltip /><Legend />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Top Products by Value</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={inventoryData.topProducts} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="name" type="category" width={80} />
                <RechartsTooltip />
                <Bar dataKey="value" fill="#2E7D32" name="Value" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );

  const renderSalesDashboard = () => (
    <Box>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Total Orders" value={salesData.totalOrders} color="#1976D2"
            icon={<SalesIcon />} onClick={() => handleDrillDown(null, 'Sales Trend')} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Total Revenue" value={salesData.totalRevenue.toFixed(2)}
            color="#2E7D32" icon={<MoneyIcon />} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Pending Orders" value={salesData.pendingOrders}
            color="#ED6C02" icon={<SalesIcon />} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Avg Order Value"
            value={(salesData.totalRevenue / (salesData.totalOrders || 1)).toFixed(2)}
            color="#7B1FA2" icon={<MoneyIcon />} />
        </Grid>
      </Grid>
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Sales Trend</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={salesData.orderTrend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" /><YAxis /><RechartsTooltip /><Legend />
                <Area type="monotone" dataKey="amount" fill="#2E7D32" stroke="#2E7D32" name="Revenue" />
                <Line type="monotone" dataKey="count" stroke="#FF8F00" name="Orders"
                  onClick={(data) => {
                    if (data?.payload?.[data.activeTooltipIndex]?.payload) {
                      handleDrillDown(data.payload[data.activeTooltipIndex].payload, 'Sales Trend',
                        data.payload[data.activeTooltipIndex].payload);
                    }
                  }} style={{ cursor: 'pointer' }} />
              </ComposedChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Order Status</Typography>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie data={salesData.statusDistribution} cx="50%" cy="50%"
                  labelLine={false} outerRadius={80} fill="#8884d8" dataKey="value">
                  {salesData.statusDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <RechartsTooltip /><Legend />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Top Customers</Typography>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={salesData.customerBreakdown}
                onClick={(data) => {
                  if (data?.payload?.[data.activeTooltipIndex]?.payload) {
                    handleDrillDown(data.payload[data.activeTooltipIndex].payload, 'Customer Breakdown',
                      data.payload[data.activeTooltipIndex].payload);
                  }
                }} style={{ cursor: 'pointer' }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" /><YAxis /><RechartsTooltip />
                <Bar dataKey="value" fill="#2E7D32" name="Revenue"
                  shape={<CustomBar onClick={(payload) => handleDrillDown(payload, 'Customer Breakdown', payload)} />} />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );

  const renderTripDashboard = () => (
    <Box>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Total Trips" value={tripData.totalTrips} color="#1976D2"
            icon={<DispatchIcon />} onClick={() => handleDrillDown(null, 'Trip Trends')} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Total Mileage (km)" value={tripData.totalMileage.toFixed(2)}
            color="#2E7D32" icon={<TimelineIcon />} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Completed Trips" value={tripData.completedTrips}
            color="#4CAF50" icon={<DispatchIcon />} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Avg Mileage"
            value={(tripData.totalMileage / (tripData.totalTrips || 1)).toFixed(2)}
            color="#7B1FA2" icon={<TimelineIcon />} />
        </Grid>
      </Grid>
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Trip Trends</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={tripData.tripTrend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" /><YAxis /><RechartsTooltip /><Legend />
                <Bar dataKey="count" fill="#1976D2" name="Trips"
                  shape={<CustomBar onClick={(payload) => handleDrillDown(payload, 'Trip Trends', payload)} />} />
                <Line type="monotone" dataKey="mileage" stroke="#FF8F00" name="Mileage"
                  onClick={(data) => {
                    if (data?.payload?.[data.activeTooltipIndex]?.payload) {
                      handleDrillDown(data.payload[data.activeTooltipIndex].payload, 'Trip Trends',
                        data.payload[data.activeTooltipIndex].payload);
                    }
                  }} style={{ cursor: 'pointer' }} />
              </ComposedChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Trip Status</Typography>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie data={tripData.statusDistribution} cx="50%" cy="50%"
                  labelLine={false} outerRadius={80} fill="#8884d8" dataKey="value">
                  {tripData.statusDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <RechartsTooltip /><Legend />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Vehicle Utilization</Typography>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={tripData.vehicleUtilization}
                onClick={(data) => {
                  if (data?.payload?.[data.activeTooltipIndex]?.payload) {
                    handleDrillDown(data.payload[data.activeTooltipIndex].payload, 'Vehicle Utilization',
                      data.payload[data.activeTooltipIndex].payload);
                  }
                }} style={{ cursor: 'pointer' }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" /><YAxis /><RechartsTooltip />
                <Bar dataKey="value" fill="#2E7D32" name="Mileage"
                  shape={<CustomBar onClick={(payload) => handleDrillDown(payload, 'Vehicle Utilization', payload)} />} />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );

  const renderTabContent = () => {
    switch (selectedTab) {
      case 0: return renderFinanceDashboard();
      case 1: return renderProductionDashboard();
      case 2: return renderProcurementDashboard();
      case 3: return renderInventoryDashboard();
      case 4: return renderSalesDashboard();
      case 5: return renderTripDashboard();
      default: return renderFinanceDashboard();
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap">
          <Box>
            <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />}>
              <Link href="#" color="inherit" onClick={() => navigate('/dashboard')}>
                <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                Dashboard
              </Link>
              <Typography color="textPrimary">Executive Dashboard</Typography>
            </Breadcrumbs>
            <Typography variant="h4" fontWeight="bold" sx={{ mt: 1 }}>Executive Dashboard</Typography>
            <Typography variant="body2" color="textSecondary">
              Click on any chart element to drill down for detailed insights
            </Typography>
          </Box>
          <Box display="flex" gap={1} flexWrap="wrap">
            <TextField type="date" label="Start Date" value={dateRange.startDate}
              onChange={handleDateChange('startDate')} size="small" sx={{ width: 150 }}
              InputLabelProps={{ shrink: true }} />
            <TextField type="date" label="End Date" value={dateRange.endDate}
              onChange={handleDateChange('endDate')} size="small" sx={{ width: 150 }}
              InputLabelProps={{ shrink: true }} />
            <Tooltip title="Refresh">
              <IconButton onClick={handleRefresh} color="primary"><RefreshIcon /></IconButton>
            </Tooltip>
            <Tooltip title="Export">
              <IconButton onClick={handleExportData} color="primary"><ExportIcon /></IconButton>
            </Tooltip>
            <Tooltip title="Print">
              <IconButton onClick={handlePrint} color="primary"><PrintIcon /></IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Paper>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={selectedTab} onChange={handleTabChange}
          variant="scrollable" scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}>
          {tabs.map((tab, index) => (
            <Tab key={index} label={tab.label} icon={tab.icon}
              iconPosition="start" sx={{ minHeight: 64 }} />
          ))}
        </Tabs>
        <Box sx={{ p: 3 }}>{renderTabContent()}</Box>
      </Paper>

      <Dialog open={drillDownOpen} onClose={handleCloseDrillDown} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6" fontWeight="bold">{drillDownTitle}</Typography>
              {selectedDataPoint && (
                <Typography variant="caption" color="textSecondary">
                  Showing details for: {selectedDataPoint.name || selectedDataPoint.date || 'Selected item'}
                </Typography>
              )}
            </Box>
            <Box>
              <Tooltip title="Export">
                <IconButton onClick={handleExportData} color="primary"><ExportIcon /></IconButton>
              </Tooltip>
              <IconButton onClick={handleCloseDrillDown}><ArrowBackIcon /></IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent>
          {drillDownLoading ? (
            <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
          ) : drillDownData && drillDownData.details && drillDownData.details.length > 0 ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    {drillDownData.columns.map((col, index) => (
                      <TableCell key={index} sx={{ fontWeight: 'bold' }}>{col}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {drillDownData.details.map((row, index) => (
                    <TableRow key={index} hover>
                      {drillDownData.columns.map((col, colIndex) => {
                        const key = drillDownData.keys[colIndex];
                        const value = row[key];
                        let display;
                        if (value === undefined || value === null) {
                          display = '—';
                        } else if (typeof value === 'number') {
                          const lower = col.toLowerCase();
                          display = (lower.includes('amount') || lower.includes('price') ||
                                     lower.includes('revenue'))
                            ? `₹${value.toFixed(2)}`
                            : value.toLocaleString();
                        } else if (typeof value === 'object') {
                          display = value.name || JSON.stringify(value);
                        } else {
                          display = value;
                        }
                        return <TableCell key={colIndex}>{display}</TableCell>;
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box textAlign="center" py={4}>
              <Typography variant="body1" color="textSecondary">
                No details available for this selection
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDrillDown} variant="contained" color="primary">Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ExecutiveDashboard;